/* eslint-disable react/prop-types */
import './index.scss';
import ModalBase from '.';
import { ModalProps } from '../../types';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from '@piggytechng/component-library';
import { capitalize, OverideButtonStyle } from '../../helpers/utils';
import CustomSelect from '../CustomSelect/CustomSelect';
import useProcessor from '../../hooks/useProcessor';
import ModalExecuteAction from './ModalExecuteAction';

function ModalSwitchProcessorBeneficiaryBank({ modalIsOpen, setIsOpen, data, action }: ModalProps) {
  const { Banks, Processors, assignBanksToProvider, isLoading } = useProcessor();
  const [showActionModal, setShowActionModal] = useState(false);

  function closeModal() {
    if (isLoading) return;
    setIsOpen(false);
  }

  const [form, setForm] = useState({
    provider: '',
    banks: [],
  });

  const allBanks = useMemo(() => {
    return (Banks || []).map((bank: any) => {
      return {
        label: bank.bm_name,
        value: bank.bm_code,
      };
    });
  }, [Banks]);

  const selectedProvider = useMemo(() => {
    if (!form.provider) return null;
    return {
      provider: form.provider,
      beneficiaryBanks: (
        (Processors || []).allProvidersInfo.find((provider: any) => provider.provider === form.provider)
          ?.assignedBanks || []
      ).map((bank: any) => {
        return {
          label: bank,
          value: allBanks.find((bnk: any) => bnk.label?.toLowerCase() === bank?.toLowerCase())?.value || '',
        };
      }),
    };
  }, [form.provider, allBanks]);

  const alreadySelectedBanks = useMemo(() => {
    return (selectedProvider?.beneficiaryBanks || []).map((bank: any) => bank.value);
  }, [selectedProvider]);

  const newlySelectedBanks = useMemo(() => {
    return form.banks.filter((code: string) => !alreadySelectedBanks.includes(code));
  }, [form.banks, alreadySelectedBanks]);

  useEffect(() => {
    setForm((prev: any) => ({ ...prev, provider: data?.provider || '' }));
  }, [data]);

  useEffect(() => {
    if (data?.provider) {
      setForm((prev: any) => ({ ...prev, banks: alreadySelectedBanks }));
    }
  }, [alreadySelectedBanks]);

  const handleSubmit = () => {
    if (!form.banks.length) {
      Alert.error('Please provide at least one bank to confirm this action.');
      return;
    }

    assignBanksToProvider(selectedProvider?.provider as string, form.banks, () => {
      Alert.success(`Banks have been assigned to ${selectedProvider?.provider} successfully`);
      action && action();
      closeModal();
    }).catch(() => {
      setShowActionModal(false);
      setIsOpen(true);
    });
  };

  return (
    <>
      <ModalBase modalIsOpen={modalIsOpen} setIsOpen={setIsOpen}>
        <div className={`modal-processor`}>
          <div className="modal-processor__header">
            <h2>Beneficiary Banks</h2>
            <button onClick={closeModal}>&times;</button>
          </div>

          <form
            className="modal-processor__body"
            onSubmit={(e) => {
              e.preventDefault();
              setShowActionModal(true);
              setIsOpen(false);
            }}
          >
            <CustomSelect
              label="Provider"
              value={form.provider}
              disabled={data?.provider ? true : false}
              options={(Processors || [])?.allProvidersInfo?.map((provider: any) => {
                return {
                  label: capitalize(provider.provider),
                  value: provider.provider,
                };
              })}
              optionLabel="label"
              optionValue="value"
              onChange={(provider: any) => setForm({ ...form, provider })}
            />

            <CustomSelect
              label="Select Beneficiary Banks"
              value={form.banks}
              disabledOptions={alreadySelectedBanks}
              onChange={(banks: any) => setForm({ ...form, banks })}
              multiple={true}
              optionLabel="label"
              optionValue="value"
              options={allBanks}
            />

            <div className="modal-processor__actions">
              <Button
                {...OverideButtonStyle}
                className="modal-processor__actions-resolve"
                type="submit"
                loading={isLoading.assignBanksToProvider}
                disabled={newlySelectedBanks.length === 0}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </ModalBase>

      <ModalExecuteAction
        modalIsOpen={showActionModal}
        isLoading={isLoading.assignBanksToProvider}
        setIsOpen={setShowActionModal}
        text="assign these banks to this provider?"
        action={() => handleSubmit()}
        actionColor="#1734CA"
        buttonText={'Proceed'}
      />
    </>
  );
}

export default ModalSwitchProcessorBeneficiaryBank;
